import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Upload, X, Image, File, Check, AlertCircle, Grid, List, Search,
  Filter, ArrowLeft, Folder, Calendar, Camera, Video, Clock, RefreshCw, Play, Square
} from 'lucide-react';
import { message, Modal, InputNumber, Select, Button, Spin, Tooltip } from 'antd';
import axios from '../../utils/axios';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const getFileIcon = (type) => (
  type?.startsWith('image/') ? <Image className="w-5 h-5" /> : <File className="w-5 h-5" />
);

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':  return <Check className="w-4 h-4 text-green-600" />;
    case 'processing': return <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />;
    case 'error':      return <AlertCircle className="w-4 h-4 text-red-600" />;
    default:           return null;
  }
};

// ── Auto-refresh interval options (in seconds) ────────────────────────────────
const AUTO_REFRESH_OPTIONS = [5, 10, 15, 30, 60];

// ── Component ─────────────────────────────────────────────────────────────────
const UploadDataPage = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));

  // ── Derived once from location / localStorage ──────────────────────────────
  const projectname = useMemo(
    () => location.state?.projectname || localStorage.getItem('projectname') || 'No Project Selected',
    [location.state?.projectname]
  );
  const organizationId = localStorage.getItem('organizationId');

  // ── Core state ──────────────────────────────────────────────────────────────
  const [uploadedFiles,       setUploadedFiles]       = useState([]);
  const [dragActive,          setDragActive]          = useState(false);
  const [loading,             setLoading]             = useState(false);
  const [uploadDataProjects,  setUploadDataProjects]  = useState([]);
  const [selectedProject,     setSelectedProject]     = useState(null);
  const [loadingProjects,     setLoadingProjects]     = useState(false);
  const [viewMode,            setViewMode]            = useState('grid');
  const [searchTerm,          setSearchTerm]          = useState('');
  const [filterType,          setFilterType]          = useState('all');
  const [showProjectView,     setShowProjectView]     = useState(false);

  // ── Automation state ────────────────────────────────────────────────────────
  const [hasActiveAutomation,  setHasActiveAutomation]  = useState(false);
  const [currentAutomation,    setCurrentAutomation]    = useState(null);
  const [checkingAutomation,   setCheckingAutomation]   = useState(false);

  // ── Live-stream modal state ────────────────────────────────────────────────
  const [isLiveStreamModalOpen, setIsLiveStreamModalOpen] = useState(false);
  const [liveStreamLoading,     setLiveStreamLoading]     = useState(false);
  const [frameRate,             setFrameRate]             = useState(1);
  const [frameUnit,             setFrameUnit]             = useState('second');
  const [duration,              setDuration]              = useState(5);
  const [durationUnit,          setDurationUnit]          = useState('minute');
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [refreshingFrames, setRefreshingFrames] = useState(false);

  // ── Auto-refresh state ─────────────────────────────────────────────────────
  const [autoRefreshEnabled,   setAutoRefreshEnabled]   = useState(false);
  const [autoRefreshInterval,  setAutoRefreshInterval]  = useState(10); // seconds
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(0);
  const autoRefreshTimerRef = useRef(null);
  const countdownTimerRef   = useRef(null);

  const fetchCameras = async () => {
    try {
      setLoading(true);
      const res = await axios.post("/fetch-cameras", {
        organizationId, projectname: projectname
      });
      if (res.data.success) {
        setCameras(res.data.cameras || []);
      } else {
        message.error(res.data.message || "Failed to fetch cameras");
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Error fetching cameras");
    } finally {
      setLoading(false);
    }
  };

  const fetchCapturedFrames = useCallback(async (silent = false) => {
    if (!currentAutomation?._id) {
      if (!silent) message.warning('No active capture found');
      return;
    }
    try {
      if (!silent) setRefreshingFrames(true);
      const res = await axios.get('/fetch-live-stream-frames', {
        params: { captureId: currentAutomation._id },
      });
      if (res.data.success) {
        const frames = res.data.frames || [];
        const formatted = frames.map((f, index) => ({
          id: `capture-${index}-${f.frameNo}`,
          name: `Captured Frame ${f.frameNo}`,
          size: f.size,
          type: 'image/jpeg',
          preview: f.url,
          status: 'completed',
          uploadDate: f.capturedAt,
          isCaptured: true,
        }));
        setUploadedFiles((prev) => {
          const existingUrls = new Set(prev.map((p) => p.preview));
          return [
            ...prev,
            ...formatted.filter((f) => !existingUrls.has(f.preview)),
          ];
        });
      }
    } catch (err) {
      if (!silent) message.error('Failed to refresh captured frames');
    } finally {
      if (!silent) setRefreshingFrames(false);
    }
  }, [currentAutomation]);

  // ── Auto-refresh logic ─────────────────────────────────────────────────────
  const stopAutoRefresh = useCallback(() => {
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setAutoRefreshCountdown(0);
    setAutoRefreshEnabled(false);
  }, []);

  const startAutoRefresh = useCallback((intervalOverride) => {
    const interval = intervalOverride || autoRefreshInterval;

    if (!currentAutomation?._id) {
      message.warning('No active capture to auto-refresh');
      return;
    }

    if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    setAutoRefreshEnabled(true);
    setAutoRefreshCountdown(interval);

    countdownTimerRef.current = setInterval(() => {
      setAutoRefreshCountdown((prev) => {
        if (prev <= 1) return interval;
        return prev - 1;
      });
    }, 1000);

    autoRefreshTimerRef.current = setInterval(() => {
      fetchCapturedFrames(true);
    }, interval * 1000);

    message.success(`Auto-refresh started every ${interval}s`);
  }, [currentAutomation, fetchCapturedFrames, autoRefreshInterval]);

  // Stop auto-refresh when automation ends
  useEffect(() => {
    if (!hasActiveAutomation && autoRefreshEnabled) {
      stopAutoRefresh();
    }
  }, [hasActiveAutomation, autoRefreshEnabled, stopAutoRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoRefreshTimerRef.current) clearInterval(autoRefreshTimerRef.current);
      if (countdownTimerRef.current)   clearInterval(countdownTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (projectname) {
      fetchCameras();
    }
  }, [projectname]);

  const fileInputRef = useRef(null);

  // ── Derived / computed ──────────────────────────────────────────────────────
  const canInteractWithUpload = !hasActiveAutomation && !loading && !!projectname;
  const projectFiles = selectedProject?.uploadDataUrl || [];

  const filteredFiles = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return uploadedFiles.filter((file) => {
      if (!file.name.toLowerCase().includes(lowerSearch)) return false;
      if (filterType === 'images')  return file.type.startsWith('image/');
      if (filterType === 'other')   return !file.type.startsWith('image/');
      return true;
    });
  }, [uploadedFiles, searchTerm, filterType]);

  const estimatedFrames = useMemo(() => {
    const durationInSeconds = durationUnit === 'hour' ? duration * 3600 : duration * 60;
    const ratePerSecond =
      frameUnit === 'second' ? frameRate :
      frameUnit === 'minute' ? frameRate / 60 :
      frameRate / 3600;
    return Math.round(ratePerSecond * durationInSeconds);
  }, [frameRate, frameUnit, duration, durationUnit]);

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchUploadData = async () => {
    setLoadingProjects(true);
    try {
      const res = await axios.post('/fetch-uploaddata', {
        username: user?.username,
        projectname,
        organizationId
      });
      if (res.status === 200) {
        setUploadDataProjects(res.data.uploadData);
      }
    } catch (err){
      message.error('Error fetching upload data');
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    fetchUploadData();
  }, [projectname]);

  useEffect(() => {
    if (!hasActiveAutomation && currentAutomation) {
      setUploadedFiles([]);
      setCurrentAutomation(null);
    }
  }, [hasActiveAutomation]);

  const previousAutomation = useRef(false);

  useEffect(() => {
    // Capture has just finished
    if (previousAutomation.current && !hasActiveAutomation) {
      fetchUploadData();
    }

    previousAutomation.current = hasActiveAutomation;
  }, [hasActiveAutomation]);

  // ── Automation polling ──────────────────────────────────────────────────────
  const checkAutomationStatus = useCallback(async () => {
    if (!projectname) {
      setHasActiveAutomation(false);
      setCurrentAutomation(null);
      return;
    }
    try {
      setCheckingAutomation(true);
      const res = await axios.get('/current-automation-status', {
        params: { username: user.username, projectname, organizationId },
      });
      // if (res.data?.success) {
      //   setHasActiveAutomation(res.data.hasActiveAutomation);
      //   if (res.data.capture) {
      //     setCurrentAutomation(res.data.hasActiveAutomation ? res.data.capture : null);
      //   }
      // }
      if (res.data?.success) {
        const running = res.data.hasActiveAutomation;

        setHasActiveAutomation(running);
        setCurrentAutomation(running ? res.data.capture : null);
      }
    } catch (err) {
      console.warn('Failed to check automation status', err);
    } finally {
      setCheckingAutomation(false);
    }
  }, [projectname]);

  useEffect(() => {
    checkAutomationStatus();
    if (!projectname) return;
    const interval = setInterval(checkAutomationStatus, 10_000);
    return () => clearInterval(interval);
  }, [checkAutomationStatus, projectname, liveStreamLoading]);

  // ── File processing ─────────────────────────────────────────────────────────
  const processFiles = useCallback((files) => {
    files.forEach((file) => {
      const id = Date.now() + Math.random();
      const fileData = {
        id,
        name: file.name,
        size: file.size,
        type: file.type,
        file,
        uploadDate: new Date().toISOString(),
        status: 'processing',
      };
      setUploadedFiles((prev) => [...prev, fileData]);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setUploadedFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, preview: e.target.result, status: 'completed' } : f))
          );
        };
        reader.readAsDataURL(file);
      } else {
        setTimeout(() => {
          setUploadedFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, status: 'completed' } : f))
          );
        }, 1000);
      }
    });
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    if (!canInteractWithUpload) return;
    processFiles(Array.from(e.dataTransfer.files));
  }, [canInteractWithUpload, processFiles]);

  const handleFileSelect = useCallback((e) => {
    if (!canInteractWithUpload) return;

    const files = Array.from(e.target.files);
    processFiles(files);

    // ✅ reset input so same file can be selected again
    e.target.value = null;

  }, [canInteractWithUpload, processFiles]);

  const removeFile = useCallback((fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  }, []);

  // ── Upload ──────────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (hasActiveAutomation) return message.warning('Cannot upload while automation is running');
    if (uploadedFiles.length === 0) return message.error('Please select files to upload');

    const manualUploadCredits = Math.ceil(uploadedFiles.length / 10);

    if (manualUploadCredits > user.credits) {
      return Modal.warning({
        centered: true,
        width: 520,
        icon: null,
        okText: 'Got it',
        title: (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-800">
                Insufficient Credits
              </div>
              <div className="text-sm text-gray-500">
                Unable to upload dataset
              </div>
            </div>
          </div>
        ),
        content: (
          <div className="mt-4 space-y-4">

            {/* Summary */}
            <div className="p-4 rounded-xl border bg-gray-50 space-y-3">

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Selected Images</span>
                <span className="font-semibold text-gray-800">
                  {uploadedFiles.length}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Upload Credits</span>
                <span className="font-medium">{manualUploadCredits}</span>
              </div>

              <div className="border-t pt-3 flex justify-between">
                <span className="font-semibold text-gray-700">
                  Total Required
                </span>
                <span className="text-lg font-bold text-red-600">
                  {manualUploadCredits}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Available Credits</span>
                <span className={`text-lg font-bold ${
                  user.credits < manualUploadCredits ? 'text-red-600' : 'text-green-600'
                }`}>
                  {user.credits}
                </span>
              </div>
            </div>

            {/* Progress */}
            <div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-500"
                  style={{
                    width: `${Math.min((user.credits / manualUploadCredits) * 100, 100)}%`
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                You need {manualUploadCredits - user.credits} more credits
              </p>
            </div>

            {/* Info */}
            <div className="text-sm text-gray-600">
              1 credit is used for every 10 images uploaded.
            </div>

            {/* Suggestion */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              💡 Reduce number of images or add more credits to continue.
            </div>

          </div>
        ),
      });
    }
    const formData = new FormData();
    uploadedFiles.forEach((f) => formData.append('file', f.file));
    formData.append('username',       user.username);
    formData.append('projectname',    projectname);
    formData.append('organizationId', organizationId);

    try {
      setLoading(true);
      const res = await axios.post('/upload-data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status === 200) {
        message.success('Files uploaded successfully!');
        setUploadedFiles([]);
        fetchUploadData();
      }
    } catch (err) {
      message.error('Error uploading files. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Live-stream capture ─────────────────────────────────────────────────────
  const handleStartLiveStreamCapture = async () => {
    if (!selectedCamera) {
      return message.error('Please select a camera');
    }

    if (frameRate < 1 || duration < 1) {
      return message.error('Frame rate and duration must be at least 1');
    }

    const captureCredits = Math.ceil(estimatedFrames / 10);
    const uploadCredits = Math.ceil(estimatedFrames / 10);

    const totalRequiredCredits = captureCredits + uploadCredits;

    if (totalRequiredCredits > user?.credits) {
      return Modal.warning({
        centered: true,
        width: 520,
        icon: null,
        okText: 'Got it',
        title: (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-800">
                Insufficient Credits
              </div>
              <div className="text-sm text-gray-500">
                Unable to start capture
              </div>
            </div>
          </div>
        ),
        content: (
          <div className="mt-4 space-y-4">

            {/* 🔹 Summary Card */}
            <div className="p-4 rounded-xl border bg-gray-50 space-y-3">

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Estimated Frames</span>
                <span className="font-semibold text-gray-800">
                  {estimatedFrames}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Capture Credits</span>
                <span className="font-medium">{captureCredits}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Upload Credits</span>
                <span className="font-medium">{uploadCredits}</span>
              </div>

              <div className="border-t pt-3 flex justify-between">
                <span className="font-semibold text-gray-700">
                  Total Required
                </span>
                <span className="text-lg font-bold text-red-600">
                  {totalRequiredCredits}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600">Available Credits</span>
                <span className={`text-lg font-bold ${
                  user?.credits < totalRequiredCredits ? 'text-red-600' : 'text-green-600'
                }`}>
                  {user?.credits}
                </span>
              </div>
            </div>

            {/* 🔹 Progress Bar */}
            <div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-red-500 transition-all"
                  style={{
                    width: `${Math.min((user?.credits / totalRequiredCredits) * 100, 100)}%`
                  }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                You need {totalRequiredCredits - user?.credits} more credits to proceed
              </p>
            </div>

            {/* 🔹 Info */}
            <div className="text-sm text-gray-600 leading-relaxed">
              Each <span className="font-medium text-gray-800">10 images = 1 credit</span> for both 
              capture and upload.
            </div>

            {/* 🔹 Suggestion */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              💡 Try reducing capture duration or frame rate to fit within your credits.
            </div>

          </div>
        ),
      });
    }

    // 🔥 NEW: Minimum frame validation
    if (estimatedFrames < 1000) {
      Modal.confirm({
        centered: true,
        width: 520,
        icon: null,
        okText: 'Start Capture Anyway',
        cancelText: 'Modify Settings',
        okButtonProps: {
          style: {
            backgroundColor: '#d97706',
            borderColor: '#d97706',
          }
        },
        title: (
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-100">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-lg font-semibold text-gray-800">
                Low Dataset Volume
              </div>
              <div className="text-sm text-gray-500">
                Capture may not meet recommended training size
              </div>
            </div>
          </div>
        ),
        content: (
          <div className="mt-4 space-y-4">
            
            {/* Frame Summary Card */}
            <div className="p-4 border rounded-lg bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Estimated Frames
                </span>
                <span className="text-xl font-semibold text-gray-800">
                  {estimatedFrames}
                </span>
              </div>

              <div className="mt-2 text-xs text-gray-500">
                Recommended minimum: <strong>1000+ frames</strong>
              </div>
            </div>

            {/* Advisory Note */}
            <div className="text-sm text-gray-600 leading-relaxed">
              Datasets with fewer than 1000 frames may lead to reduced model
              accuracy, limited variation coverage, and weaker AI performance.
              <br />
              <span className="text-gray-800 font-medium">
                Consider increasing duration or frame rate for better results.
              </span>
            </div>

          </div>
        ),
        onOk: () => startCaptureRequest(),
      });

      return;
    }

    // If >= 500 frames → directly start
    startCaptureRequest();
  };

  const startCaptureRequest = async () => {
    try {
      setLiveStreamLoading(true);

      await axios.post('/start-live-stream-capture', {
        username: user.username,
        projectname,
        frameRate,
        frameUnit,
        duration,
        durationUnit,
        cameraId: selectedCamera,
        organizationId
      });

      message.success(
        `Live capture started! ${estimatedFrames} frames estimated.`
      );

      setIsLiveStreamModalOpen(false);

    } catch (err) {
      message.error('Failed to start live stream capture');
    } finally {
      setLiveStreamLoading(false);
    }
  };

  const resetLiveStreamForm = useCallback(() => {
    setFrameRate(1);
    setFrameUnit('minute');
    setDuration(5);
    setDurationUnit('minute');
  }, []);

  // ── Navigation helpers ──────────────────────────────────────────────────────
  const handleAnnotateClick = useCallback((project) => {
    if (!project.uploadDataUrl?.length) return alert('No files to annotate');
    navigate('/sidebar/Annotation', {
      state: {
        images:          project.uploadDataUrl.map(({ url, width, height }) => ({ url, width, height })),
        projectName:     project.projectname,
        projectType:     project.projectType,
        annotationGroup: project.annotationGroup,
        version:         project.version,
      },
    });
  }, [navigate]);

  const handleProjectClick = useCallback((project) => {
    setSelectedProject(project);
    setShowProjectView(true);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
        {/* ── Automation banner ─────────────────────────────────────────────── */}
        {hasActiveAutomation && !showProjectView && (
          <div className="mb-8 p-5 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl shadow-sm">
            <div className="flex items-start gap-4">
              <div className="mt-1">
                <div className="w-6 h-6 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 text-lg flex items-center gap-2">
                  Automation Running in Background
                  {checkingAutomation && <Spin size="small" />}
                </h3>
                <p className="text-amber-700 mt-1">
                  Capturing {currentAutomation?.frameRate} frame{currentAutomation?.frameRate !== 1 && 's'} per {currentAutomation?.frameUnit}
                  {' • '}for {currentAutomation?.duration} {currentAutomation?.durationUnit}
                </p>
                <p className="text-sm text-amber-600 mt-2">
                  Upload and new capture requests are disabled until this process finishes.
                </p>
              </div>

              <button
                onClick={async () => {
                  try {
                    setLoading(true);

                    const res = await axios.post('/stop-live-stream-capture', {
                      captureId: currentAutomation?._id
                    });

                    if (res.data.success) {
                      message.success('Capture stopped. Uploading captured images...');

                      // Stop auto refresh
                      stopAutoRefresh();

                      // Wait 2 seconds to allow backend finalize
                      setTimeout(async () => {

                        // Refresh dataset list
                        await fetchUploadData();

                        // Completely reload page
                        window.location.reload();

                      }, 2000);
                    }

                  } catch (err) {
                    message.error('Failed to stop capture');
                  } finally {
                    setLoading(false);
                  }
                }}
                className="px-5 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition"
              >
                Stop Capture
              </button>
            </div>
          </div>
        )}

        {/* ── Project detail view ───────────────────────────────────────────── */}
        {showProjectView && (
          <div>
            {/* Back button */}
            <div className="flex items-center mb-6">
              <button
                onClick={() => {
                  setShowProjectView(false);
                  setSelectedProject(null);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg
                          bg-white border border-gray-200 text-gray-700
                          hover:bg-gray-50 hover:shadow transition"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Datasets
              </button>
            </div>
            {projectFiles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {projectFiles.map((file, idx) => (
                  <div key={idx} className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all overflow-hidden group">
                    <div className="aspect-square bg-gray-100 overflow-hidden">
                      <img src={file.url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-sm truncate">File {idx + 1}</h3>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Folder className="w-20 h-20 text-gray-300 mx-auto mb-5" />
                <h3 className="text-xl font-semibold text-gray-800">No files in this project</h3>
                <p className="text-gray-600 mt-2">Upload files to get started</p>
              </div>
            )}
          </div>
        )}

        {/* ── Upload section ────────────────────────────────────────────────── */}
        {!showProjectView && (
          <div className="mb-10">

            {/* Action buttons row */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center mb-6 items-center">
              <div className="flex flex-wrap gap-3 mt-2 sm:mt-0 items-center">

                {/* Create Dataset Using AI */}
                <button
                  onClick={() => {
                    if (hasActiveAutomation) return message.warning('Automation already running');
                    resetLiveStreamForm();
                    setIsLiveStreamModalOpen(true);
                  }}
                  disabled={liveStreamLoading || hasActiveAutomation || uploadedFiles.length > 0}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold shadow transition-all min-w-[220px] justify-center
                    ${hasActiveAutomation || uploadedFiles.length > 0
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : liveStreamLoading
                        ? 'bg-gray-400 text-white cursor-wait'
                        : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:brightness-110 text-white hover:shadow-lg'
                    }`}
                >
                  {liveStreamLoading ? <Spin size="small" /> : <Camera size={18} />}
                  {liveStreamLoading ? 'Starting...' : hasActiveAutomation ? 'Automation Running' : 'Create Dataset Using AI'}
                </button>

                {/* Upload Dataset */}
                <button
                  onClick={handleUpload}
                  disabled={!canInteractWithUpload || uploadedFiles.length === 0}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold shadow transition-all min-w-[180px] justify-center
                    ${!canInteractWithUpload || uploadedFiles.length === 0
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : loading
                        ? 'bg-gray-400 text-white cursor-wait'
                        : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-110 text-white hover:shadow-lg'
                    }`}
                >
                  {loading ? <Spin size="small" /> : <Upload size={18} />}
                  {loading ? 'Uploading...' : 'Upload Dataset'}
                </button>

                {/* ── Refresh Controls ──────────────────────────────────────── */}
                {/* Manual Refresh */}
                <Tooltip title="Manually fetch latest captured frames">
                  <button
                    onClick={() => fetchCapturedFrames(false)}
                    disabled={!currentAutomation || refreshingFrames || autoRefreshEnabled}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all shadow
                      ${!currentAutomation || autoRefreshEnabled
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : refreshingFrames
                          ? 'bg-gray-400 text-white cursor-wait'
                          : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg hover:brightness-110'
                      }`}
                  >
                    {refreshingFrames ? <Spin size="small" /> : <RefreshCw size={16} />}
                    Refresh Captured Frames
                  </button>
                </Tooltip>

                {/* Auto Refresh section */}
                <div className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all shadow-sm
                  ${autoRefreshEnabled
                    ? 'border-emerald-400 bg-emerald-50'
                    : 'border-gray-200 bg-white'
                  }`}
                >
                  {/* Interval selector */}
                  <Tooltip title="Auto-refresh interval">
                    <Select
                      value={autoRefreshInterval}
                      onChange={(val) => {
                        const interval = Number(val);

                        setAutoRefreshInterval(interval);

                        if (autoRefreshEnabled) {
                          stopAutoRefresh();
                          setTimeout(() => startAutoRefresh(interval), 50);
                        }
                      }}
                      disabled={!currentAutomation}
                      size="small"
                      className="w-28"
                      suffixIcon={<Clock size={12} />}
                    >
                      {AUTO_REFRESH_OPTIONS.map((s) => (
                        <Select.Option key={s} value={s}>
                          {s}s interval
                        </Select.Option>
                      ))}
                    </Select>
                  </Tooltip>

                  {/* Toggle auto-refresh on/off */}
                  {autoRefreshEnabled ? (
                    <Tooltip title="Stop auto-refresh">
                      <button
                        onClick={stopAutoRefresh}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 font-semibold text-sm transition-all"
                      >
                        <Square size={13} />
                        Stop
                        {/* Countdown ring */}
                        <span className="ml-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold">
                          {autoRefreshCountdown}
                        </span>
                      </button>
                    </Tooltip>
                  ) : (
                    <Tooltip title={!currentAutomation ? 'No active capture' : `Auto-refresh every ${autoRefreshInterval}s`}>
                      <button
                        onClick={() => startAutoRefresh()}
                        disabled={!currentAutomation}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all
                          ${!currentAutomation
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                          }`}
                      >
                        <Play size={13} />
                        Auto
                      </button>
                    </Tooltip>
                  )}
                </div>

              </div>
            </div>

            {/* Auto-refresh active indicator strip */}
            {autoRefreshEnabled && (
              <div className="mb-4 flex items-center gap-3 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 font-medium">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                Auto-refresh active — next refresh in
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white text-xs font-bold">
                  {autoRefreshCountdown}
                </span>
                seconds
                <button
                  onClick={stopAutoRefresh}
                  className="ml-auto text-xs text-red-500 hover:text-red-700 underline"
                >
                  Stop
                </button>
              </div>
            )}

            {/* Drop zone */}
            <div
              className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all
                ${dragActive ? 'border-blue-500 bg-blue-50/60 scale-[1.015]' : 'border-gray-300'}
                ${!canInteractWithUpload ? 'opacity-50 pointer-events-none bg-gray-50' : 'hover:border-blue-400 hover:bg-blue-50/30'}`}
              onDragEnter={(e) => { e.preventDefault(); if (canInteractWithUpload) setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={!canInteractWithUpload}
                accept='.jpeg, .jpg, .png, .webp'
              />
              {hasActiveAutomation ? (
                <div className="py-10 space-y-4">
                  <AlertCircle className="w-14 h-14 text-amber-500 mx-auto" />
                  <h3 className="text-xl font-semibold text-gray-800">Upload disabled during automation</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    Files are being captured from the live stream. Upload will be re-enabled once the process completes.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center transition
                    ${dragActive ? 'bg-blue-600 text-white scale-110' : 'bg-gray-100 text-gray-600'}`}>
                    <Upload className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900">
                    {dragActive ? 'Drop files here' : 'Upload your files'}
                  </h3>
                  <p className="text-gray-600">Drag & drop or click to select files</p>
                  <p className="text-sm text-gray-500">Supports images (JPG, PNG, WebP) and other file types</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Staged-file controls ─────────────────────────────────────────── */}
        {!showProjectView && uploadedFiles.length > 0 && (
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
              >
                <option value="all">All Files</option>
                <option value="images">Images Only</option>
                <option value="other">Other Files</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* ── Staged files grid / list ──────────────────────────────────────── */}
        {!showProjectView && filteredFiles.length > 0 && (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6' : 'space-y-4'}>
            {filteredFiles.map((file) => (
              <div
                key={file.id}
                className={`bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 overflow-hidden group ${
                  viewMode === 'list' ? 'flex items-center p-4' : 'p-4'
                }`}
              >
                {viewMode === 'grid' ? (
                  <>
                    <div className="aspect-square bg-gray-100 rounded-xl mb-4 overflow-hidden relative">
                      {file.preview ? (
                        <img src={file.preview} alt={file.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">{getFileIcon(file.type)}</div>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-2 left-2">{getStatusIcon(file.status)}</div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm truncate mb-1" title={file.name}>{file.name}</h3>
                      <p className="text-xs text-gray-600">{formatFileSize(file.size)}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      {file.preview ? (
                        <img src={file.preview} alt={file.name} className="w-full h-full object-cover rounded-lg" />
                      ) : getFileIcon(file.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate" title={file.name}>{file.name}</h3>
                      <p className="text-sm text-gray-600">
                        {formatFileSize(file.size)} • {new Date(file.uploadDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(file.status)}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="w-8 h-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex items-center justify-center transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Empty states ──────────────────────────────────────────────────── */}
        {!showProjectView && uploadedFiles.length === 0 && uploadDataProjects.length === 0 && (
          <div className="text-center py-16">
            <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No files uploaded yet</h3>
            <p className="text-gray-600">Upload your first files to get started</p>
          </div>
        )}

        {!showProjectView && uploadedFiles.length > 0 && filteredFiles.length === 0 && (
          <div className="text-center py-16">
            <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No files match your filters</h3>
            <p className="text-gray-600">Try adjusting your search or filter settings</p>
          </div>
        )}

        {/* ── Uploaded-projects grid ────────────────────────────────────────── */}
        {!showProjectView && uploadDataProjects.length > 0 && (
          <div className="mb-8 mt-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Uploaded DataSets</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {uploadDataProjects.map((project) => (
                <div
                  key={project._id}
                  onClick={() => handleProjectClick(project)}
                  className="group cursor-pointer bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-blue-300 transition-all duration-300 overflow-hidden transform hover:-translate-y-1"
                >
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <Folder className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate text-lg" title={project.projectname}>
                          {project.projectname}_{project.version}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center text-gray-500 text-sm">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(project.createdAt).toLocaleDateString()}
                      </div>
                      <span className="text-sm font-medium text-blue-600">
                        {project.uploadDataUrl?.length || 0} files
                      </span>
                    </div>
                    {project.uploadDataUrl?.length > 0 && (
                      <div className="grid grid-cols-4 gap-1 mb-3">
                        {project.uploadDataUrl.slice(0, 4).map((file, idx) => {
                          const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(file.url);
                          return (
                            <div key={idx} className="aspect-square bg-gray-100 rounded overflow-hidden relative">
                              {isImage ? (
                                <img src={file.url} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <File className="w-4 h-4" />
                                </div>
                              )}
                              {file.width && file.height && (
                                <div className="absolute bottom-0 right-0 bg-black bg-opacity-50 text-white text-[10px] px-1 rounded-tl">
                                  {file.width}×{file.height}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {project.uploadDataUrl.length > 4 && (
                          <div className="aspect-square bg-gray-100 rounded flex items-center justify-center text-xs text-gray-600 font-medium">
                            +{project.uploadDataUrl.length - 4}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="pt-3 border-t border-gray-100 flex gap-3">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleProjectClick(project); }}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                      >
                        View Files
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAnnotateClick(project); }}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                      >
                        Annotate the images
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Live-stream modal ─────────────────────────────────────────────── */}
        <Modal
          title={
            <div className="flex items-center gap-3 text-lg font-semibold">
              <Video className="w-6 h-6 text-emerald-600" />
              Automate Data from Live Stream
            </div>
          }
          open={isLiveStreamModalOpen}
          onCancel={() => setIsLiveStreamModalOpen(false)}
          footer={null}
          centered
          width={480}
        >
          <div className="space-y-6 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Camera</label>
              <Select
                placeholder="Choose a camera"
                value={selectedCamera}
                onChange={setSelectedCamera}
                className="w-56"
                loading={!cameras.length}
              >
                {cameras.map((cam) => (
                  <Select.Option key={cam.channel} value={cam.channel}>
                    {cam.channel}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Capture Frequency</label>
              <div className="flex gap-3">
                <InputNumber min={1} max={2000} value={frameRate} onChange={setFrameRate} className="w-24" />
                <Select value={frameUnit} onChange={setFrameUnit} className="w-32">
                  <Select.Option value="minute">per minute</Select.Option>
                  <Select.Option value="hour">per hour</Select.Option>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Capture Duration</label>
              <div className="flex gap-3">
                <InputNumber min={1} max={durationUnit === 'hour' ? 72 : 999} value={duration} onChange={setDuration} className="w-24" />
                <Select value={durationUnit} onChange={setDurationUnit} className="w-32">
                  <Select.Option value="minute">minutes</Select.Option>
                  <Select.Option value="hour">hours</Select.Option>
                </Select>
              </div>
            </div>
            <div className="pt-4 border-t flex justify-end gap-3">
              <Button onClick={() => setIsLiveStreamModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleStartLiveStreamCapture}
                loading={liveStreamLoading}
                className="bg-gradient-to-r from-emerald-600 to-teal-600"
              >
                <Clock className="w-4 h-4 mr-2" />
                Start Capture
              </Button>
            </div>
            <p className='text-xs text-gray-500 text-center'>
              Maximum capture duration is 72 hours.
            </p>
          </div>
        </Modal>
      </main>
    </div>
  );
};

export default UploadDataPage;